import { Grid } from '~/engine/data/common/Grid';
import type { RoiId } from './changes/ids';
import { compressSlices } from './sdfCompression';

/** An ROI's captured original mask: compressed per-slice SDF pixels + its grid. */
export interface RoiBaseline {
    readonly compressedPixels: Uint8Array;
    readonly grid: Grid;
}

/**
 * Session store of the ORIGINAL mask of each ROI the app's own AI auto-segmented
 * this session (an AUTOMATIC ROI in a structure set that carries a generation
 * model — an uploaded RTStruct's AUTOMATIC ROIs do NOT qualify, see the capture
 * gate in useRoiHandlers), captured once from its first edit — before the mask is
 * changed — so the "vs original AI" difference overlay can compare a structure's
 * current contour against what the AI predicted. Independent of the undo stack
 * (which is capped and evicts) and never overwritten; cleared on patient unload.
 *
 * Baselines are keyed by the structure's {@link RoiId}. Masks are stored
 * compressed (the SDF is mostly saturated, so it packs well), matching how the
 * undo history stores its snapshots.
 *
 * Capture is asynchronous (worker compression), so a listener that peeks the
 * store on the edit event alone would read it before the mask lands. Listeners
 * {@link subscribe} to be notified once a capture completes and can then recompute
 * — mirroring {@link ContourUndoService}'s notification for the pre-edit snapshot.
 */
export class RoiBaselineStore {
    private readonly baselines = new Map<RoiId, RoiBaseline>();
    /**
     * Keys with a capture in flight, each held against the token of the call
     * that reserved it. The token — rather than a bare key set — lets a capture
     * detect that its slot was invalidated (see {@link clearRoi}) while it was
     * awaiting compression, and abandon its now-stale result.
     */
    private readonly capturing = new Map<RoiId, symbol>();
    private readonly listeners = new Set<() => void>();

    /**
     * Capture an ROI's original mask. No-op if already captured, capture is
     * already in flight, or the mask is empty. Compression runs in a worker
     * (see {@link compressSlices}) so the first edit of a large AI structure
     * does not jank the UI. The in-flight guard reserves the slot synchronously
     * so a second edit arriving before compression finishes cannot overwrite the
     * true original with a post-edit mask. Notifies listeners once the mask lands
     * so an overlay that depends on it can draw without waiting for an unrelated
     * re-render.
     */
    async capture(
        roiId: RoiId,
        pixels: Uint8Array[],
        grid: Grid
    ): Promise<void> {
        if (
            this.baselines.has(roiId) ||
            this.capturing.has(roiId) ||
            pixels.length === 0
        ) {
            return;
        }
        const token = Symbol(roiId);
        this.capturing.set(roiId, token);
        try {
            const compressedPixels = await compressSlices(pixels);
            // The ROI can be deleted while compression runs, so a slot
            // invalidated mid-flight (token no longer ours) abandons its
            // result instead of landing a deleted structure's mask.
            if (this.capturing.get(roiId) !== token) {
                return;
            }
            this.baselines.set(roiId, { compressedPixels, grid });
            this.notifyListeners();
        } finally {
            if (this.capturing.get(roiId) === token) {
                this.capturing.delete(roiId);
            }
        }
    }

    get(roiId: RoiId): RoiBaseline | undefined {
        return this.baselines.get(roiId);
    }

    /**
     * Subscribe to capture-completed notifications. Fires after any baseline
     * lands (global, not per-ROI — a spurious wake just triggers a cheap
     * recompute). Returns an unsubscribe handle.
     */
    subscribe(listener: () => void): { unsubscribe: () => void } {
        this.listeners.add(listener);
        return {
            unsubscribe: () => {
                this.listeners.delete(listener);
            },
        };
    }

    private notifyListeners(): void {
        this.listeners.forEach((listener) => listener());
    }

    /**
     * Drops the captured baseline of a deleted ROI, and any capture still in
     * flight, so a compression that completes after the deletion cannot
     * re-populate the slot.
     */
    clearRoi(roiId: RoiId): void {
        this.baselines.delete(roiId);
        this.capturing.delete(roiId);
    }

    clear(): void {
        this.baselines.clear();
        this.capturing.clear();
    }
}
